import React, {useEffect, useState} from 'react';
import {getActiveJobs} from "../../api/jobs";
import {JobStatus, JobType} from "../../schemas/enums";
import {useGroupContext} from "../../contexts/group_context";

const STORAGE_KEY = "demoSeedingGroupId";

/**
 * Shown on the dashboard right after signup while the user's "Pre-uploaded Demo
 * Playground" group is being cloned in the background (SEED job). Polls that group's
 * active jobs; when the SEED job finishes it refreshes the group list (so the new docs
 * appear) and switches to a brief "ready" message before disappearing.
 */
const DemoSeedingBanner: React.FC = () => {
    const {refreshUserGroups} = useGroupContext()!;
    const [groupId, setGroupId] = useState<string | null>(() => localStorage.getItem(STORAGE_KEY));
    const [progress, setProgress] = useState(0);
    const [done, setDone] = useState(false);

    useEffect(() => {
        if (!groupId) return;
        let cancelled = false;
        let timer: ReturnType<typeof setTimeout>;

        // The demo workspace already exists (it's created at signup), so surface it in
        // the dashboard list right away rather than waiting for the seed job to finish.
        void refreshUserGroups();

        const poll = async () => {
            try {
                const jobs = await getActiveJobs(groupId);
                if (cancelled) return;
                const seed = jobs.find(j => j.type === JobType.SEED &&
                    (j.status === JobStatus.PROCESSING || j.status === JobStatus.QUEUED));
                if (seed) {
                    setProgress(seed.progress ?? 0);
                    timer = setTimeout(poll, 4000);
                } else {
                    // No active seed job left → clone finished.
                    localStorage.removeItem(STORAGE_KEY);
                    await refreshUserGroups();
                    if (cancelled) return;
                    setDone(true);
                    timer = setTimeout(() => setGroupId(null), 5000);
                }
            } catch {
                if (!cancelled) timer = setTimeout(poll, 6000);
            }
        };
        void poll();
        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [groupId, refreshUserGroups]);

    const dismiss = () => {
        localStorage.removeItem(STORAGE_KEY);
        setGroupId(null);
    };

    if (!groupId) return null;

    return (
        <div className="mx-4 mb-3 flex items-center gap-3 rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-3 text-sm text-indigo-900">
            <span className="text-lg">{done ? "✅" : "✨"}</span>
            <div className="flex-1">
                {done ? (
                    "Your demo workspace is ready — open it from your workspaces below."
                ) : (
                    <>
                        <div>
                            Setting up your demo workspace in the background — documents will
                            appear shortly. You can keep using the app in the meantime and
                            create your own workspace anytime.
                        </div>
                        <div className="mt-1.5 h-1.5 w-full max-w-xs overflow-hidden rounded bg-indigo-100">
                            <div
                                className="h-full bg-indigo-500 transition-all duration-500"
                                style={{width: `${Math.max(8, Math.round(progress * 100))}%`}}
                            />
                        </div>
                    </>
                )}
            </div>
            <button
                onClick={dismiss}
                aria-label="Dismiss"
                className="text-indigo-400 hover:text-indigo-700"
            >
                &times;
            </button>
        </div>
    );
};

export default DemoSeedingBanner;
